import { useCallback, useEffect, useMemo, useState, useRef, useContext, forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import {
  CalendarNav,
  CalendarNext,
  CalendarPrev,
  Eventcalendar,
  momentTimezone,
  Page,
} from "@mobiscroll/react";
import {IconStatus, singleEventSkeleton, NO_EQUIPMENT_FOR_FILTER, APP_ROUTES} from "../../constants";
import BookingScheduleStatus from "../book-equipment/calendar-timeline/BookingScheduleStatus";
import { OwcButton, OwcIcon, OwcModalDialog, OwcTypography, OwcHyperlink, OwcTooltip } from "@one/react";
import { useSelector } from "react-redux";
import BackdropPortal from "../../components/shared/BackdropPortal";
import PopoverBookingTitle from "../../components/shared/CommonPopOverModal/PopoverBookingTitle";
import PopOverModal from "../../components/shared/CommonPopOverModal/PopOverModal";
import useDialog from "../../utils/hooks/useDialog";
import PopoverBookingStatusDetail from "../../components/shared/CommonPopOverModal/PopoverBookingStatusDetail";
import { valueOrEmpty, formatUserDisplay, buildProjectsLabel } from "../../utils/helpers/text";
import moment from "moment";
import { GET_BOOKING_DATA_BY_SITE } from "../../gql/bookingapi";
import { GET_BOOKING_ENTRIES_BY_BOOKING_ID } from "../../gql/bookingapi/queries";
import { useGraphQLErrorHandling } from "../../components/error-handling";
import { withApollo } from "react-apollo";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { changeActionTypeToDisplay, getBookingCategory, isActionType, formatBookingForDisplay } from "../../components/shared/helpFunctions";
import useInstrumentCache from "../../utils/hooks/useInstrumentCache";
import { useSharedFilterState } from "./SharedFilterContext";
import { BookingContext } from "../booking/context";
import { quickEditTracker } from "../booking-operations/quickEditTracker";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { loadPendingReservations as loadPendingReservationsAction } from "../booking-calendars/redux/actions";
import './BookingGrid.scss'
import { debounce } from "lodash";
import { BOOKING_OPERATIONS, useBookingOperations } from "../booking-operations";
import { bookingOperationsService } from "../booking-operations/bookingOperationsService";
import { Auth } from "aws-amplify";
import { DROP_BOOKING } from "../../gql/bookingapi/mutations";
import ConfirmDialog from "../../components/shared/ConfirmDialog";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { getRepetitionIcon } from "../../utils/helpers/getRepetitionIcon";
import getRRuleDisplayText from "../../utils/helpers/text/transformRRuleToText";
import { BookerBadge } from '../book-equipment/calendar-timeline/components/badges/BookerBadge';

const MainViewStyled = styled.div`
display: flex;
flex-direction: column;
width: 100%;
flex-grow: 1;
`;

const CalendarContainer = styled.div`
display: flex;
flex-direction: column;
flex: 1;
`


const MessageSection = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  font-style: italic;
`;

/**
 * Generate initials from email or full name
 * @param {string} emailOrName - Email address or full name
 * @returns {string} - User initials (max 2 characters)
 */
const getUserInitials = (emailOrName) => {
  if (!emailOrName) return "??";

  // If it looks like an email, extract the part before @
  if (emailOrName.includes("@")) {
    const username = emailOrName.split("@")[0];
    // Split by common separators and take first 2 initials
    const parts = username.split(/[._-]/).filter(part => part.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return username.substring(0, 2).toUpperCase();
  }

  // If it's a full name, split by spaces and take first letter of each word
  const words = emailOrName.trim().split(/\s+/).filter(word => word.length > 0);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  // Fallback: take first 2 characters
  return emailOrName.substring(0, 2).toUpperCase();
};

const BookingGrid = ({
  client,
  eventID = null,
  isEquipmentView,
  viewData,
  savedFilter,
  signeeFilter,
  setPopupEnableStatus = () => { },
  loadPendingReservations,
  isGroupView = false,
  testEventData = null,
  onOperationStart = () => { },
  onOperationComplete = () => { },
  forwardedRef
}) => {
  const dataTimeZone = "utc";
  const [myEvents, setEvents] = useState([]);

  // Initialize error handling for GraphQL operations
  const { handleGetAllData } = useGraphQLErrorHandling('BookingGrid');
  const [noEquipmentForFilter, setNoEquipmentForFilter] = useState(false);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const user = useSelector((store) => store.user);
  const usersList = useSelector((state) => state.users.users);
  const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");
  const dialogControls = useDialog();
  const { openDialog, close } = dialogControls;
  const [loading, setLoading] = useState(false);
  const displayTimeZone = user.timezone;
  const calendarRef = useRef(null);
  const [calendarInstance, setCalendarInstance] = useState(null);
  const [popoverObject, setPopoverObject] = useState({
    equipmentData: {},
    eventData: {},
  });

  // Use shared filter state
  const {
    filterState,
    isFilterDataReady,
    loadFilterData: loadSharedFilterData,
    updateCurrentFilter,
    getCurrentFilter
  } = useSharedFilterState();

  const { createBookingCallback } = useBookingOperations();

  const [isUpdatingAssistance, setIsUpdatingAssistance] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);

  const assistanceUpdateCallbackRef = useRef(null);
  const cancellationCallbackRef = useRef(null);
  const pendingModalRefreshRef = useRef(null); // Track booking ID that needs modal refresh

  // Handle when assistance update mutation is sent
  const handleAssistanceUpdateStarted = useCallback(() => {
    setIsUpdatingAssistance(true);
  }, []);

  // Initialize instrument cache hook
  const instrumentCache = useInstrumentCache({
    client,
    user,
    masterDataFilters: filterState.masterDataFilters
  });

  // Refs for managing async operations and state
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const lastLoadedParamsRef = useRef(null);
  const currentDateRangeRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  const view = useMemo(() => {
    return {
      schedule: { type: "week", allDay: false }
    };
  }, []);

  const resolveProjectText = (data) => {
    const row = data || {};
    return buildProjectsLabel({
      rowRedPandaProjects: row.redPandaProjects,
      userRedPandaProjects: user?.redPandaProjects,
      projectId: row.project,
    });
  };

  // Debounced version of the loader
  const debouncedLoad = useRef(
    debounce((df, dt, filter, signee) => loadBookingsData(df, dt, filter, signee), 400)
  ).current;

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      debouncedLoad.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setNoEquipmentForFilter(false);
    };
  }, [debouncedLoad]);

  // Load filter data on mount
  useEffect(() => {
    if (!isFilterDataReady && user?.site && user?.id) {
      loadSharedFilterData();
    }
  }, [loadSharedFilterData, isFilterDataReady, user?.site, user?.id]);

  // Update current filter when savedFilter changes
  useEffect(() => {
    if (isFilterDataReady) {
      const newFilter = updateCurrentFilter(savedFilter);

      // Trigger data reload only if not initial load and date range is set
      if (!isInitialLoadRef.current && currentDateRangeRef.current) {
        const { dateFrom, dateTo } = currentDateRangeRef.current;
        debouncedLoad(dateFrom, dateTo, newFilter, signeeFilter);
      }
    }
  }, [savedFilter, signeeFilter, isFilterDataReady, updateCurrentFilter, debouncedLoad]);

  // Update modal with fresh data when myEvents changes and we have a pending refresh
  useEffect(() => {
    if (pendingModalRefreshRef.current && myEvents.length > 0) {
      const bookingIdToRefresh = pendingModalRefreshRef.current;

      setPopoverObject(prev => {
        if (!prev?.eventData?.id) {
          return prev;
        }

        // Find the updated booking in the fresh events
        const freshBooking = myEvents.find(event => event.id === bookingIdToRefresh);

        if (freshBooking) {
          return {
            ...prev,
            eventData: {
              ...prev.eventData,
              assistent: freshBooking.assistent,
              assistentTime: freshBooking.assistentTime,
            }
          };
        }

        return prev;
      });

      // Clear the pending refresh
      pendingModalRefreshRef.current = null;
    }
  }, [myEvents]);

  const loadBookingsData = useCallback(async (dateFrom, dateTo, activeFilter, signee, skipLoadingState = false, forceRefresh = false) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const currentParams = {
      dateFrom,
      dateTo,
      filterName: activeFilter?.filterName || null,
      signeeUserId: signee?.userId || null,
      isEquipmentView
    };

    if (!forceRefresh && lastLoadedParamsRef.current &&
      JSON.stringify(lastLoadedParamsRef.current) === JSON.stringify(currentParams)) {
      return Promise.resolve();
    }

    // Don't set loading state if we're already in a loading state (e.g., from assistant update)
    if (!skipLoadingState) {
      setLoading(true);
    }

    try {
      if (signal.aborted) return;

      let filteredBookings = [];

      if (!activeFilter || activeFilter.filterName === "" || !activeFilter.filterDefinition) {
        // No equipment filter - show bookings based on signee selection
        const bookingFilter = {
          and: [
            { active: { eq: true } },
            {
              and: [{ dateFrom: { le: dateTo } }, { dateTo: { ge: dateFrom } }],
            }
          ]
        };

        // Always include reservedForUsers filter
        if (signee?.userId && signee.userId !== 'null' && signee.userId !== '') {
          bookingFilter.and.push({ reservedForUsers: { contains: signee.userId } });
        }

        const queryVariables = {
          filter: JSON.stringify(bookingFilter),
          dateFrom: dateTo,
          site: user?.site || null,
          limit: 1000,
          nextToken: null,
        };

        const { items } = await handleGetAllData({
          client,
          query: GET_BOOKING_DATA_BY_SITE,
          dataPath: ["data", "getBookingDataBySite"],
          variables: queryVariables,
          drillData: true,
          cache: !forceRefresh,
          signal // Pass abort signal
        });

        if (signal.aborted) return;

        filteredBookings = items;
        if (mountedRef.current) {
          setNoEquipmentForFilter(false);
        }
      } else {
        // Use saved filter to get instrument IDs first
        const loadInstrumentsResponse = await instrumentCache.loadInstruments(
          user.site,
          activeFilter.filterDefinition,
          { signal }
        )
        if (!loadInstrumentsResponse) {
          dispatchAction({
            type: "fetching",
            payload: false,
          });
          return
        }
        const { items: filteredInstruments } = loadInstrumentsResponse;

        if (signal.aborted) return;

        const availableInstrumentIds = filteredInstruments.map(item => item.inventoryId);

        if (availableInstrumentIds.length === 0) {
          filteredBookings = [];
          if (mountedRef.current) {
            setNoEquipmentForFilter(true);
          }
        } else {
          if (mountedRef.current) {
            setNoEquipmentForFilter(false);
          }
          const bookingFilter = {
            and: [
              { active: { eq: true } },
              {
                and: [{ dateFrom: { le: dateTo } }, { dateTo: { ge: dateFrom } }],
              }
            ]
          };

          if (signee?.userId && signee.userId !== 'null' && signee.userId !== '') {
            bookingFilter.and.push({ reservedForUsers: { contains: signee.userId } });
          }

          const queryVariables = {
            filter: JSON.stringify(bookingFilter),
            dateFrom: dateTo,
            site: user?.site || null,
            limit: 1000,
            nextToken: null,
          };

          const { items: bookingItems } = await handleGetAllData({
            client,
            query: GET_BOOKING_DATA_BY_SITE,
            dataPath: ["data", "getBookingDataBySite"],
            variables: queryVariables,
            drillData: true,
            signal,
            cache: !forceRefresh,
          });

          if (signal.aborted) return;

          // Filter bookings to only include those with equipment from saved filter
          filteredBookings = bookingItems.filter(booking => {
            if (!booking.bookingEntries || booking.bookingEntries.length === 0) {
              return false;
            }

            return booking.bookingEntries.some(entry => {
              if (entry.equipment) {
                const equipmentId = entry.inventoryId || entry.equipment.inventoryId;
                return equipmentId && availableInstrumentIds.includes(equipmentId);
              }
              return false;
            });
          });
        }
      }

      if (signal.aborted) return;

      // Process bookings for display
      const newModifiedData = filteredBookings.map((item) => formatBookingForDisplay(item));

      if (signal.aborted || !mountedRef.current) return;

      setEvents(newModifiedData);
      lastLoadedParamsRef.current = currentParams;
      return Promise.resolve();

    } catch (error) {
      if (error.name === 'AbortError') {
        return Promise.resolve();
      }
      console.error("BookingGrid: Error loading bookings:", error);
      if (mountedRef.current) {
        setEvents([]);
        setNoEquipmentForFilter(false);
      }
      return Promise.reject(error);
    } finally {
      if (mountedRef.current && !skipLoadingState) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.site, client, instrumentCache, isEquipmentView]);

  // This effect responds to filter changes, but skips the initial load.
  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }

    if (!isFilterDataReady || !currentDateRangeRef.current) {
      return;
    }

    // This is now handled in the effect that updates the filter
    // to avoid race conditions with context state updates.
  }, [signeeFilter, isEquipmentView, isFilterDataReady, debouncedLoad]);

  // Calendar page loading handler
  const onPageLoading = useCallback(async (event) => {
    debouncedLoad.cancel();
    const dateFrom = moment(event.firstDay).subtract(1, "days").utc().format();
    const dateTo = moment(event.lastDay).add(1, "days").utc().format();

    currentDateRangeRef.current = { dateFrom, dateTo };

    // This is the primary trigger for the initial data load
    if (isFilterDataReady) {
      // Replaced filterState.currentFilter with a direct call to getCurrentFilter
      // to avoid race conditions on remount.
      const activeFilter = getCurrentFilter(savedFilter);
      await loadBookingsData(dateFrom, dateTo, activeFilter, signeeFilter);
    }

    // After the first load attempt, allow the filter-change effect to run
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }
  }, [isFilterDataReady, loadBookingsData, debouncedLoad, signeeFilter, getCurrentFilter, savedFilter]);

  const handleAssistantUpdate = useCallback(async (data) => {
    const bookingIdToRefresh = popoverObject?.eventData?.id;

    const subscriptionData = data?.data?.onUpdateBookingCommand;
    if (!subscriptionData) return;

    onOperationStart?.({ type: 'UPDATE', operation: BOOKING_OPERATIONS.UPDATE, loading: true });
    setIsUpdatingAssistance(true);

    const updateCallback = createBookingCallback({
      operation: BOOKING_OPERATIONS.UPDATE,
      resetStates: {
        status: setIsUpdatingAssistance,
      },
      onSuccess: async (_data) => {
        if (assistanceUpdateCallbackRef.current) {
          assistanceUpdateCallbackRef.current();
          assistanceUpdateCallbackRef.current = null;
        }

        setIsUpdatingAssistance(false);

        setTimeout(async () => {
          try {
            if (currentDateRangeRef.current) {
              const { dateFrom, dateTo } = currentDateRangeRef.current;

              if (bookingIdToRefresh) {
                pendingModalRefreshRef.current = bookingIdToRefresh;
              }

              await loadBookingsData(dateFrom, dateTo, getCurrentFilter(savedFilter), signeeFilter, false, true);
            }
            onOperationComplete?.({ type: 'UPDATE', operation: BOOKING_OPERATIONS.UPDATE, success: true, data: _data });
          } catch (error) {
            console.error("BookingGrid: Error during data refresh", error);
            onOperationComplete?.({ type: 'UPDATE', operation: BOOKING_OPERATIONS.UPDATE, success: false, error });
          }
        }, 100);
      },
      onError: (error) => {
        setIsUpdatingAssistance(false);
        onOperationComplete?.({ type: 'UPDATE', operation: BOOKING_OPERATIONS.UPDATE, success: false, error });
      },
      skipCheckInSucceeded: true,
    });

    updateCallback(data);
  }, [createBookingCallback, loadBookingsData, getCurrentFilter, savedFilter, signeeFilter, popoverObject, onOperationStart, onOperationComplete]);

  // Internal handler for cancellations
  const handleCancellation = useCallback(async (data) => {
    const subscriptionData = data?.data?.onUpdateBookingCommand;
    if (!subscriptionData || !cancellationCallbackRef.current) return;

    const processingResult = subscriptionData.processingResult;
    if (processingResult !== 'ALL_SUCCEEDED') return;

    onOperationStart?.({ type: 'CANCEL', operation: BOOKING_OPERATIONS.CANCEL, loading: true });

    const userCallback = cancellationCallbackRef.current;
    cancellationCallbackRef.current = null;

    const cancelCallback = createBookingCallback({
      operation: BOOKING_OPERATIONS.CANCEL,
      debounce: false,
      resetStates: {
        status: setIsCancelling,
      },
      skipNotification: false,
      onSuccess: async (_data) => {
        await userCallback();
        onOperationComplete?.({ type: 'CANCEL', operation: BOOKING_OPERATIONS.CANCEL, success: true, data: _data });
      },
      onError: (error) => {
        setIsCancelling(false);
        setCancelReason("");
        onOperationComplete?.({ type: 'CANCEL', operation: BOOKING_OPERATIONS.CANCEL, success: false, error });
      },
    });

    cancelCallback(data);
  }, [createBookingCallback, onOperationStart, onOperationComplete]);

  // Internal handler for other quick edits
  const handleOtherQuickEdit = useCallback(async (data) => {
    const subscriptionData = data?.data?.onUpdateBookingCommand;
    if (!subscriptionData) return;

    const commandId = subscriptionData.id;

    if (!commandId || !quickEditTracker.isQuickEdit(commandId)) return;

    const operation = quickEditTracker.getOperation(commandId);

    bookingOperationsService.handleResponse(data, {
      operation,
      skipNotification: false
    }, {});

    if (currentDateRangeRef.current) {
      const { dateFrom, dateTo } = currentDateRangeRef.current;
      await loadBookingsData(dateFrom, dateTo, getCurrentFilter(savedFilter), signeeFilter, false, true);
    }

    onOperationComplete?.({ type: operation, operation, success: true, data });
  }, [loadBookingsData, getCurrentFilter, savedFilter, signeeFilter, onOperationComplete]);

  // Expose methods to parent via ref
  useImperativeHandle(forwardedRef, () => ({
    handleSubscriptionEvent: async (data, operation) => {
      const subscriptionData = data?.data?.onUpdateBookingCommand;

      if (!subscriptionData) {
        return;
      }

      const processingResult = subscriptionData.processingResult;
      const commandId = subscriptionData.id;

      if (cancellationCallbackRef.current && processingResult === 'ALL_SUCCEEDED') {
        await handleCancellation(data);
        return;
      }

      if (assistanceUpdateCallbackRef.current) {
        await handleAssistantUpdate(data);
        return;
      }

      if (commandId && quickEditTracker.isQuickEdit(commandId)) {
        await handleOtherQuickEdit(data);
        return;
      }

      bookingOperationsService.handleResponse(data, {
        operation,
        skipNotification: false
      }, {});

      if (currentDateRangeRef.current) {
        const { dateFrom, dateTo } = currentDateRangeRef.current;
        await loadBookingsData(dateFrom, dateTo, getCurrentFilter(savedFilter), signeeFilter, false, true);
      }
      onOperationComplete?.({ type: operation, operation, success: true, data });
    },

    refreshCalendar: async (options = {}) => {
      if (currentDateRangeRef.current) {
        const { dateFrom, dateTo } = currentDateRangeRef.current;
        await loadBookingsData(dateFrom, dateTo, getCurrentFilter(savedFilter), signeeFilter, false, true);
      }
    },

    updateBooking: async (bookingId) => {
      try {
        const result = await client.query({
          query: GET_BOOKING_ENTRIES_BY_BOOKING_ID,
          variables: { id: bookingId },
          fetchPolicy: 'network-only'
        });

        if (result?.data?.getBooking) {
          setEvents(prev => prev.map(e =>
            e.id === bookingId ? { ...e, ...result.data.getBooking } : e
          ));
        }
      } catch (error) {
        console.error("BookingGrid: Error updating booking", error);
        snackbarService.show({ type: 'error', message: 'Failed to update booking', duration: 3000 });
      }
    },

    getState: () => ({
      isLoading: loading,
      isUpdatingAssistance,
      isCancelling,
      currentBooking: popoverObject?.eventData
    })
  }), [handleCancellation, handleAssistantUpdate, handleOtherQuickEdit, loadBookingsData, getCurrentFilter, savedFilter, signeeFilter, client, loading, isUpdatingAssistance, isCancelling, popoverObject, onOperationComplete]);

  const onEventClick = useCallback((event) => {
    let eventData = {
      ...event?.event,
      reservedForEmails: (event?.event?.bookedUsers || [])?.map((k) => k?.email).filter(Boolean),
    };
    openDialog();
    setPopoverObject({
      equipmentData: event?.event,
      eventData: eventData,
    });
  }, [openDialog]);

  const renderScheduleEvent = useCallback((data) => {
    let labelColor = IconStatus?.filter((k) => k.text === getBookingCategory(data?.original?.type));

    // Check if booking is pending approval
    const isPending = data?.original?.approval?.status === 'PENDING' ||
      data?.original?.bookingApprovalInfo?.approval?.status === 'PENDING';

    // Get assistant info (check both possible locations)
    const assistantId = data?.original?.assistent?.[0] ||
      data?.original?.bookingApprovalInfo?.assistent;

    // Map assistant ID to display name
    const getAssistantDisplay = (assistId) => {
      if (!assistId || !usersList) return '';
      const assistantUser = usersList.find(user => user.userId === assistId);
      if (assistantUser) {
        if (assistantUser.givenName && assistantUser.familyName) {
          return `${assistantUser.givenName} ${assistantUser.familyName}`;
        }
        return assistantUser.email || assistId;
      }
      return assistId;
    };

    const assistantDisplay = getAssistantDisplay(assistantId);
    const assistantInitials = assistantDisplay && assistantDisplay.length > 1 ?
      assistantDisplay.split(/[\s.@]+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() : '';
    const recurranceText = getRRuleDisplayText(data?.original?.repetitionPattern, new Date(data?.original?.dateFrom));

    return (
      <div
        className="md-custom-event-cont"
        style={{
          width: "100%",
          height: "100%",
          background: isPending ? "#FFFFFF" : data?.color,
          border: isPending ? "2px solid #7D0096" : "none",
          boxSizing: "border-box",
        }}
      >
        <div className="md-custom-event-wrapper">
          <div className="md-custom-event-category">
            <div style={{ position: 'relative', display: 'inline-block', marginRight: '8px' }}>
              {/* Assistant badge in top-left corner */}
              {assistantInitials && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-3px',
                    left: '-8px',
                    backgroundColor: '#fff',
                    border: '1px solid #7D0096',
                    borderRadius: '5px',
                    width: '22px',
                    height: '21px',
                    fontSize: '6px',
                    fontWeight: '600',
                    color: '#7D0096',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    lineHeight: '1',
                    zIndex: 15,
                  }}
                  title={`Assistant: ${assistantDisplay}${isPending ? ' (Pending Approval)' : ''}`}
                >
                  {assistantInitials}
                </div>
              )}
              <div style={{ width: "47px", height: "47px" }}>
                <OwcIcon
                  type="outlined"
                  name={data?.original?.icon}
                  style={{
                    minWidth: 47,
                    minHeight: 47,
                    borderRadius: "5px",
                    fontSize: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1px solid ${isPending ? labelColor?.[0]?.color : 'white'} `,
                    color: labelColor?.[0]?.color,
                  }}
                />
              </div>
              {/* User initials positioned over the icon */}
              {data?.original?.bookedUsers && data?.original?.bookedUsers.length > 0 && (() => {
                const userIdentifiers = data?.original?.bookedUsers.map(user => {
                  if (!user) return '';
                  if (user.fullName || user.givenName || user.familyName) {
                    return user.fullName || `${user.givenName || ''} ${user.familyName || ''}`.trim();
                  }
                  return user.email || user.name || '';
                }).filter(Boolean);
                const uniqueUsers = [...new Set(userIdentifiers)];
                const displayUser = uniqueUsers[0];
                return (
                  <BookerBadge
                    initials={getUserInitials(displayUser)}
                    hasMultipleUsers={uniqueUsers.length > 1}
                    isPending={isPending}
                    fontColor={labelColor?.[0]?.color || '#7D0096'}
                    isFirstTimeBooking={data?.original?.isFirstTimeUser || false}
                    style={{ top: '30px', left: '28px' }}
                  />
                );
              })()}
            </div>
            <div className="md-custom-event-title">
              <OwcTypography variant="badge" style={{ color: labelColor?.[0]?.color }}>
                <strong>{getBookingCategory(data?.original?.type)}</strong>
              </OwcTypography>

              <div className="md-custom-event-time" style={{ color: labelColor?.[0]?.color }}>
                <OwcTypography variant="caption">
                  <div style={{ display: 'flex', paddingLeft: '5px'}}>
                    {recurranceText && (
                      <>
                        {getRepetitionIcon(recurranceText)}
                      </>
                    )}
                    {data?.start === "" && data?.end === "" ? (
                      <> {data?.allDayText?.replaceAll(":00", "")}</>
                    ) : (
                      <>
                        {data?.start?.replace(":00", "")} - {data?.end?.replace(":00", "")}
                      </>
                    )}
                  </div>

                </OwcTypography>
              </div>
            </div>
          </div>
          <div className="md-custom-event-details">
            <div className="md-custom-event-description">
              <OwcTypography variant="caption" style={{ color: labelColor?.[0]?.color }}>
                Description
              </OwcTypography>
              <OwcTypography variant="subtitle2" style={{ color: labelColor?.[0]?.textColor }}>
                {data?.original?.description}
              </OwcTypography>
            </div>

            <div className="md-custom-event-description">
              <OwcTypography variant="caption" style={{ color: labelColor?.[0]?.color }}>
                {data?.original?.redPandaProjects?.length ? "Projects" : "Project"}
              </OwcTypography>

              {(() => {
                const projectText = resolveProjectText(data?.original);
                const tooltipId = `booking-project-${data?.original?.id}`;
                const projectLabelStyle = { maxWidth: "120px", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
                return (
                  <>
                    <OwcTypography
                      variant="subtitle2"
                      id={tooltipId}
                      style={{ ...projectLabelStyle, color: labelColor?.[0]?.textColor }}
                    >
                      {projectText}
                    </OwcTypography>

                    {projectText && projectText !== "-" && (
                      <OwcTooltip anchor={tooltipId}>
                        <span style={{ whiteSpace: "pre-line" }}>
                          {projectText?.replaceAll(", ", "\n")}
                        </span>
                      </OwcTooltip>
                    )}
                  </>
                );
              })()}
            </div>

            {isActionType(data?.original?.type) && (
              <div>
                <OwcTypography variant="caption" style={{ color: labelColor?.[0]?.color }}>
                  Action Type
                </OwcTypography>
                <OwcTypography variant="subtitle2" style={{ color: labelColor?.[0]?.textColor }}>
                  {valueOrEmpty(changeActionTypeToDisplay(data?.original?.type), false, "-")}
                </OwcTypography>
              </div>
            )}
            <div className="md-custom-event-description">
              <OwcTypography variant="caption" style={{ color: labelColor?.[0]?.color }}>
                Number of equipment
              </OwcTypography>
              <OwcTypography variant="subtitle2" style={{ color: labelColor?.[0]?.textColor }}>
                {data?.original?.noEquipment}
              </OwcTypography>
            </div>
          </div>
        </div>
      </div>
    ); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersList]);

  const handleCalendarInit = useCallback((event) => {
    setCalendarInstance(event.inst);
  }, []);

  const navigateToToday = useCallback(() => {
    if (!calendarInstance) return;

    const today = moment().tz(displayTimeZone).toDate();

    calendarInstance.navigate(today);
  }, [calendarInstance, displayTimeZone]);

  const renderMyHeader = useCallback(() => {
    return (
      <>
        <CalendarNav className="md-work-week-nav" />
        <CalendarPrev className="md-work-week-prev" />
        <div
          className="md-work-week-today custom-nav-button today-button"
          onClick={navigateToToday}
          style={{ cursor: 'pointer' }}
        >
          Today
        </div>
        <CalendarNext className="md-work-week-next" />

        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}>
          <MessageSection>
            <OwcIcon name="info_outline" style={{ marginRight: '8px', fontSize: '18px' }} />
            <OwcTypography variant="body1">
              {noEquipmentForFilter ? NO_EQUIPMENT_FOR_FILTER : ``}
            </OwcTypography>
          </MessageSection>
        </div>
      </>
    );
  }, [navigateToToday, noEquipmentForFilter]);

  const [checkinEventNotFound, setCheckinEventNotFound] = useState(false);

  useEffect(() => {
    if (testEventData) {
      setPopoverObject({ equipmentData: testEventData, eventData: testEventData });
    }
  }, [testEventData]);

  useEffect(() => {
    if (myEvents.length > 0 && !!eventID) {
      const event = myEvents.find((event) => event.id === eventID);

      if (!!event) {
        const eventData = {
          ...event,
          reservedForEmails: event?.bookedUsers?.map((k) => k.email),
        };
        openDialog();
        setPopoverObject({
          equipmentData: event,
          eventData: eventData,
        });
      } else {
        setCheckinEventNotFound(true);
      }
    }
  }, [myEvents, eventID, openDialog]);

  const history = useHistory();

  const routeChange = useCallback((path) => {
    history.push(path);
  }, [history]);

  const { dispatchAction: bookingDispatchAction } = useContext(BookingContext);
  const { dispatchAction } = useContext(PaginationContext);

  const startLoading = useCallback(() => {
    setLoading(true);
    if (dispatchAction) {
      dispatchAction({
        type: "fetching",
        payload: true,
      });
    }
  }, [dispatchAction]);

  const stopLoading = useCallback(() => {
    setLoading(false);
    if (dispatchAction) {
      dispatchAction({
        type: "clearAll",
      });
    }
  }, [dispatchAction]);

  const emailToBookingForObj = (email) => {
    const found = usersList.find((u) => u.email === email);
    return found ? {
      ...found,
      key: found.email,
      value: formatUserDisplay(found),
    } : {
      key: email,
      userId: null,
      email,
      name: email,
      givenName: "",
      familyName: "",
      value: email,
    };
  };

  const editBooking = async (editData) => {
    let editDataClone = { ...editData };

    // Prepare the edit data structure
    editDataClone['bookedEquipments'] = editDataClone.bookedEquipments || [];

    const extraFieldsToRemove = [
      'allDay', 'color', 'editable', 'createdAt', 'createdByEmail',
      'end', 'expiresAt', 'fcolor', 'recurringException',
      'recurringExceptionRule', 'resource', 'site', 'start',
      'timezone', 'title', 'tooltip', 'icon'
    ];
    extraFieldsToRemove.forEach(k => delete editDataClone[k]);

    const currentTimeString = moment.tz(user.timezone).utc().format();
    dispatchAction({
      type: "fetching",
      payload: true,
    });

    try {
      const { items } = await handleGetAllData({
        client,
        query: GET_BOOKING_ENTRIES_BY_BOOKING_ID,
        drillData: true,
        fetchPolicy: "no-cache",
        variables: {
          bookingId: editDataClone.id,
          nextToken: null,
          filter: { active: { eq: true } },
        },
        cache: true,
        dataPath: ["data", "bookingEntriesByBookingId"],
      });

      if (items.length > 0) {
        const keyArray = Object.keys(singleEventSkeleton);
        editDataClone["bookingEntries"] = items.map((item) => {
          let tempObj = {};
          keyArray.forEach((key) => {
            tempObj = {
              ...tempObj,
              [key]: item[key] ?? null,
            };
          });
          tempObj = {
            ...tempObj,
            inventoryId: item.resource,
            equipment: item.equipment,
            title: item.type,
            description: item?.description,
            elnId: item?.elnId,
            project: item?.project,
            redPandaProjects: item?.redPandaProjects,
            createdBy: item?.createdBy,
            reservedForEmails: item?.reservedForEmails,
          };
          return tempObj;
        });
        const bookedUsersList = (editDataClone.reservedForEmails || []).map(emailToBookingForObj);
        editDataClone.bookingFor = bookedUsersList;
        editDataClone.bookedUsers = bookedUsersList;
        if (items[0].elnId) editDataClone.elnId = items[0].elnId;
      }

      bookingDispatchAction({
        type: "editBookingData",
        payload: {
          isEditMode: true,
          editBookingData: editDataClone,
          isOngoing: currentTimeString > editDataClone.dateFrom && currentTimeString < editDataClone.dateTo,
        },
      });

      const tempBookingEntries = editDataClone?.bookingEntries?.map((item) => {
        return {
          ...item,
          inventoryId: item.resource,
          isEditEventFlag: true,
          isOngoingEventFlag:
            (currentTimeString > item.end && currentTimeString > item.start) ||
            (currentTimeString > item.start && currentTimeString < item.end),
          editable: currentTimeString < item.end,
          title: item?.type,
        };
      });

      loadPendingReservations(tempBookingEntries || []);
      if (editDataClone?.bookingEntries) history.push("/");
    } catch (error) {
      console.error("Error editing booking:", error);
    }
  };

  const renderModalActions = () => {
    const eventData = popoverObject?.eventData;
    if (!eventData || Object.keys(eventData).length === 0) return null;

    const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");
    const isCreator = eventData?.createdBy === user?.user || eventData?.createdByEmail === user?.email;
    const isBookedForUser = eventData?.reservedForEmails?.includes(user?.email);

    if (isBookingAdmin || isCreator || isBookedForUser) {
      return (
        <div style={{ display: "flex" }}>
          <OwcButton
            variant="secondary"
            flat={true}
            style={{ marginLeft: "20px" }}
            data-testid="booking-grid-cancel-booking-button"
            onClick={() => setCancelOpen(true)}
          >
            Cancel Booking
          </OwcButton>
          <OwcButton
            variant="primary"
            style={{ marginLeft: "20px" }}
            onClick={() => {
              setPopupEnableStatus(true);
              editBooking(eventData);
            }}
          >
            Open Full Edit
          </OwcButton>
        </div>
      );
    }

    return null;
  };

  // End of component body continues below

  return (
    <>
      <MainViewStyled>
        <>
          <BackdropPortal show={loading} />
          <CalendarContainer>
            <Page>
              <Eventcalendar
                ref={calendarRef}
                onInit={handleCalendarInit}
                theme="ios"
                renderHeader={renderMyHeader}
                themeVariant="light"
                clickToCreate={false}
                dragToCreate={false}
                dragToMove={false}
                dragToResize={false}
                eventDelete={false}
                data={myEvents}
                view={view}
                onEventClick={onEventClick}
                renderScheduleEvent={renderScheduleEvent}
                dataTimezone={dataTimeZone}
                displayTimezone={displayTimeZone}
                timezonePlugin={momentTimezone}
                eventOverlap={false}
                onPageLoading={onPageLoading}
              />
            </Page>
          </CalendarContainer>
          {isLegendVisible && <BookingScheduleStatus />}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "start", marginLeft: "4px" }}>
            <OwcHyperlink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsLegendVisible(!isLegendVisible);
              }}
            >
              {isLegendVisible ? 'Hide legend' : 'Show legend'}
            </OwcHyperlink>
          </div>

        </>
      </MainViewStyled>

      <PopOverModal
        title={Object.keys(popoverObject?.equipmentData).length > 0 && <PopoverBookingTitle data={popoverObject} />}
        closeTitle={"Close"}
        data={popoverObject}
        showEmailButton={false}
        actions={renderModalActions()}
        {...dialogControls}
        onDialogCloseCallBack={() =>
          setPopoverObject({
            equipmentData: {},
            eventData: {},
          })
        }
      >
        <>
          <PopoverBookingStatusDetail
            data={popoverObject}
            equipmentDetails={true}
            onAssistanceUpdateStarted={handleAssistanceUpdateStarted}
            assistanceUpdateCallbackRef={assistanceUpdateCallbackRef}
            allowAssistanceEdit={isBookingAdmin}
            startLoading={startLoading}
            stopLoading={stopLoading}
          />
        </>
      </PopOverModal>
      <ConfirmDialog
        open={cancelOpen}
        close={() => setCancelOpen(false)}
        title="Cancel booking"
        content={`Please add reason for canceling booking`}
        approveText="Drop booking"
        cancelText="Close"
        showReasonDD={true}
        reasonValue={cancelReason}
        onReasonChanged={(r) => setCancelReason(r)}
        disableBooking={!cancelReason || isCancelling}
        onApprove={async () => {
          const eventData = popoverObject?.eventData;
          const bookingId = eventData?.bookingId || eventData?.id || eventData?.entryId;
          if (!bookingId) return;

          startLoading();
          setIsCancelling(true);

          cancellationCallbackRef.current = async () => {
            try {
              if (currentDateRangeRef.current) {
                const { dateFrom, dateTo } = currentDateRangeRef.current;
                await loadBookingsData(dateFrom, dateTo, getCurrentFilter(savedFilter), signeeFilter, false, true);

                if (calendarRef.current?.instance?.refresh) {
                  calendarRef.current.instance.refresh();
                }
              } else {
                console.warn('BookingGrid: currentDateRangeRef.current is not set, cannot refresh');
              }
            } catch (error) {
              console.error("BookingGrid: Error refreshing data after cancellation", error);
            } finally {
              setCancelOpen(false);
              close();
              setIsCancelling(false);
              setCancelReason("");
              stopLoading();
            }
          };

          try {
            const { username: id } = await Auth.currentAuthenticatedUser();
            await client.mutate({
              mutation: DROP_BOOKING,
              variables: { createdBy: id, bookingId, remark: cancelReason, ...(user?.roles?.includes("BookingAdmin") ? { force: true } : {}) },
            });
          } catch (e) {
            console.error('BookingGrid: DROP_BOOKING mutation failed', e);
            cancellationCallbackRef.current = null;
            setIsCancelling(false);
            setCancelReason("");
            stopLoading();
            try { snackbarService.show({ type: 'error', message: 'Cancellation failed', duration: 3000 }); } catch (_) { }
          }
        }}
      />
      {checkinEventNotFound && (
        <OwcModalDialog visible={true} size={"sm"} className="owcmodalZIndex" disableBackdropClick={true}>
          <div slot="header">Error</div>
          <div>Booking event not found</div>

          <div
            slot="actions"
            style={{
              display: "flex",
              justifyContent: "end",
            }}
          >
            <OwcButton
              onClick={() => {
                setCheckinEventNotFound(false);
                routeChange(APP_ROUTES.MY_BOOKINGS);
              }}
            >
              OK
            </OwcButton>
          </div>
        </OwcModalDialog>
      )}
    </>
  );
};

const BookingGridWithApollo = withApollo(BookingGrid);

const BookingGridWithRef = forwardRef((props, ref) => (
  <BookingGridWithApollo {...props} forwardedRef={ref} />
));

export default connect(null, {
  loadPendingReservations: loadPendingReservationsAction,
}, null, { forwardRef: true })(BookingGridWithRef);
